import React, { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/store.model';
import {
  getComposableDeviceIsLoadingByPropGroupIdSelectorById,
  getMachineTypeSelector,
} from '../../redux/composableDevice/selectors/composableDeviceSelectors';
import { useParams } from 'react-router-dom';
import DeckOven from './Oven/DeckOven';
import { Box, Stack, Typography } from '@mui/material';
import DraggableList, { Directions } from './DraggableList';
import { DropResult } from 'react-beautiful-dnd';
import { ComposableDeviceUserInterfaces } from '../../redux/composableDevice/ComposableDevice.model';
import FryerPan from './Fryer/FryerPan';
import { getPolyglot } from '../../i18n';
import { useGroupById } from '../../redux/groups/hooks/useGroupById';
import { putGroup } from '../../redux/groups/actions/thunks';

function ComposableDevicePage() {
  const params = useParams<{ groupId?: string }>();
  const { data: group } = useGroupById(params.groupId ?? '');
  const polyglot = getPolyglot();
  const dispatch = useAppDispatch();
  const machineType = useAppSelector((state) =>
    getMachineTypeSelector(state, {
      machineTypeId: group?.composableDevice?.machineTypeId,
    })
  );
  // FIXME: if is need
  const isLoadingComposableDevice = useAppSelector((state) =>
    getComposableDeviceIsLoadingByPropGroupIdSelectorById(state, params)
  );
  const reorder = (list: string[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  function compareArrays(array1: any[], array2: any[]) {
    return (
      array1.length === array2.length &&
      array1.every(
        (value, index) => value + array2[index] === array2[index] + value
      )
    );
  }

  const items = useMemo(() => group?.devices ?? [], [group?.devices]);

  const onDragEnd = async ({ destination, source }: DropResult) => {
    // dropped outside the list
    if (!destination) return;

    const newItems = reorder(items, source.index, destination.index);

    !compareArrays(newItems, items) &&
      (await dispatch(
        putGroup(group?.id ?? '', { ...group, devices: newItems }, undefined)
      ));
  };

  if (!group) {
    return <></>;
  }

  return (
    <Stack
      height="100%"
      width="100%"
      sx={{
        backgroundColor: (theme) => theme.palette.grey[100],
        padding: '0 2rem',
      }}
    >
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        padding={1}
        sx={{
          boxShadow: '0px 6px 6px #00000027',
          mb: '0.5rem',
          backgroundColor: (theme) => theme.palette.common.white,
        }}
      >
        <Box display={'flex'}>
          <div>
            {group?.iconURL ? (
              <Box
                component="img"
                sx={{ mr: 1, verticalAlign: 'middle', height: '4.5em' }}
                src={group?.iconURL}
                alt="added logo"
              ></Box>
            ) : null}
          </div>
          <Box
            display={'flex'}
            justifyContent={'center'}
            flexDirection={'column'}
          >
            <Typography variant="h2" mb={0}>
              {group?.composableDevice?.labels?.businessModel}{' '}
              {group?.composableDevice?.labels?.businessModel && '-'}{' '}
              {group?.name}
            </Typography>

            <Typography variant="body1">
              {group?.devices.length}{' '}
              {polyglot.t(
                `composable_device.${
                  machineType?.userInterface ===
                    ComposableDeviceUserInterfaces.OVEN ||
                  machineType?.userInterface ===
                    ComposableDeviceUserInterfaces.FR_OVEN
                    ? 'decks'
                    : 'pans'
                }`
              )}
            </Typography>
          </Box>
        </Box>
      </Box>
      <div className="composable-device-panel">
        {machineType?.userInterface === ComposableDeviceUserInterfaces.OVEN ||
        machineType?.userInterface ===
          ComposableDeviceUserInterfaces.FR_OVEN ? (
          <DraggableList
            items={items.map((deck) => (
              <DeckOven
                key={deck}
                deviceId={deck}
                isLoadingComposableDevice={isLoadingComposableDevice}
              />
            ))}
            direction={Directions.COLUMN}
            onDragEnd={onDragEnd}
          />
        ) : (
          <DraggableList
            items={items.map((pan) => (
              <FryerPan
                key={pan}
                deviceId={pan}
                isLoadingComposableDevice={isLoadingComposableDevice}
              />
            ))}
            direction={Directions.ROW}
            onDragEnd={onDragEnd}
          />
        )}
      </div>
      <Box
        padding={1}
        display={'flex'}
        justifyContent={'flex-end'}
        sx={{
          boxShadow: '0px -6px 6px #00000026',
          minHeight: '3.5rem',
          backgroundColor: (theme) => theme.palette.common.white,
        }}
      ></Box>
    </Stack>
  );
}

export default ComposableDevicePage;
